import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { message } from 'antd';
import { AppDispatch, RootState, resetReduxState } from './store';
import { subscribeToAuthEvents, type AuthEventDetail } from './utils/authEventBroadcast';
import { useLoadingAppData, useIdleLogout } from './hooks';
import AuthLayout from './components/auth/AuthLayout';
import IdleTimeoutModal from './components/common/IdleTimeoutModal';
import SignInPage from './pages/auth/SignInPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import OTPVerificationPage from './pages/auth/OTPVerificationPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ChangeTemporaryPasswordPage from './pages/auth/ChangeTemporaryPasswordPage';
//import OnboardingPage from './pages/auth/OnboardingPage';
import DashboardLayout from './components/dashboard/DashboardLayout';
import CompaniesPage from './pages/exhibits/company/CompaniesPage';
import DashboardReportingPage from './pages/exhibits/ex1/DashboardReportingPage';
import FinancialStatementsPage from './pages/exhibits/ex2/FinancialStatementsPage';
import ProcessHierarchyPage from './pages/exhibits/ex3/ProcessHierarchyPage';
import RiskAssessmentPage from './pages/exhibits/ex4/RiskAssessmentPage';
import RiskAssessmentDetailPage from './pages/exhibits/ex4/RiskAssessmentDetailPage';
import RiskAssessmentTotalPage from './pages/exhibits/ex4/RiskAssessmentTotalPage';
import ControlActivitiesPage from './pages/exhibits/ex5/ControlActivitiesPage';
import ControlActivityItemPage from './pages/exhibits/ex5/ControlActivityItemPage';
import BulkWeightingManagementPage from './pages/exhibits/ex5/BulkWeightingManagementPage';
import GapsPage from './pages/exhibits/ex6/GapsPage';
import GapItemPage from './pages/exhibits/ex6/GapItemPage';
import CertificationPage from './pages/exhibits/ex7/CertificationPage';
import COSOCompliancePage from './pages/exhibits/ex8/COSOCompliancePage';
import AuditPlanPage from './pages/exhibits/ex9/AuditPlanPage';
import GlobalSearchPage from './pages/exhibits/search/GlobalSearchPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import AssertionManagementPage from './pages/globalSettings/AssertionManagementPage';
import UserManagementPage from './pages/exhibits/userManagement/UserManagementPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import NotFoundPage from './pages/NotFoundPage';
import LoadingScreen from './components/common/LoadingScreen';
import SyncfusionGridTest from './components/test/SyncfusionGridTest';
import SentryTest from './components/test/SentryTest';

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const requiresOnboarding = user?.requiresOnboarding ?? false;
  const passwordChangeRequired = user?.passwordChangeRequired ?? false;

  // Load app data (restore auth + fetch annual reports, etc.)
  const { sessionLoading } = useLoadingAppData();

  // Inactivity auto-logout (only active when authenticated and not onboarding)
  const {
    showWarning: showIdleWarning,
    remainingTime: idleRemainingTime,
    handleStayLoggedIn,
  } = useIdleLogout({ disabled: !isAuthenticated });

  // Subscribe to auth events from other tabs (always active)
  // Note: No dependencies to ensure subscription stays active across re-renders
  useEffect(() => {
    // console.log('🔧 Setting up auth event subscription');
    const unsubscribe = subscribeToAuthEvents((event: AuthEventDetail) => {
      // console.log('📡 App received auth event:', event);
      switch (event.type) {
        case 'auth:logout':
          // Another tab logged out - sync this tab
          // console.log('🚪 Syncing logout from another tab');
          // Clear all Redux state with single action
          dispatch(resetReduxState());
          // Show notification
          message.info('You have been signed out from another tab');
          break;
        case 'auth:login':
          // Another tab logged in - reload to sync auth state
          // console.log('🔓 Another tab logged in');
          // Reload page to restore auth state
          message.info('You have signed in from another tab');
          setTimeout(() => {
            window.location.reload();
          }, 500);
          break;
        case 'auth:refresh':
          // Token was refreshed in another tab - storage is already updated
          // console.log('🔄 Token refreshed in another tab');
          break;
      }
    });
    
    // Cleanup on unmount only
    return () => {
      // console.log('🔧 Cleaning up auth event subscription');
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - run once and keep subscription alive

  if (sessionLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className='app'>
      <IdleTimeoutModal
        open={showIdleWarning}
        remainingTime={idleRemainingTime}
        onStayLoggedIn={handleStayLoggedIn}
      />
      <Routes>
        {/* Test Routes - No authentication required */}
        <Route path='/test/syncfusion' element={<SyncfusionGridTest />} />
        <Route path='/test/sentry' element={<SentryTest />} />

        {/* Authentication Routes */}
        <Route
          path='/auth/*'
          element={
            !isAuthenticated ? (
              <AuthLayout>
                <Routes>
                  <Route path='signin' element={<SignInPage />} />
                  <Route
                    path='forgot-password'
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path='verify-otp'
                    element={<OTPVerificationPage />}
                  />
                  <Route
                    path='reset-password'
                    element={<ResetPasswordPage />}
                  />
                  <Route
                    path='change-temporary-password'
                    element={<ChangeTemporaryPasswordPage />}
                  />
                  <Route path='*' element={<Navigate to='/auth/signin' />} />
                </Routes>
              </AuthLayout>
            ) : (
              <Navigate to={passwordChangeRequired ? '/change-password' : '/companies'} replace />
            )
          }
        />

        {/* Change Password Route - authenticated users who must change password */}
        <Route
          path='/change-password'
          element={
            !isAuthenticated ? (
              <Navigate to='/auth/signin' replace />
            ) : requiresOnboarding ? (
              <Navigate to='/onboarding' replace />
            ) : passwordChangeRequired ? (
              <ChangePasswordPage />
            ) : (
              <Navigate to='/companies' replace />
            )
          }
        />

        {/* Protected Routes */}
        <Route
          path='/*'
          element={
            isAuthenticated ? (
               (
                passwordChangeRequired ? (
                  <Navigate to='/change-password' replace />
              ) : (
              <DashboardLayout>
                <Routes>
                  <Route path='companies' element={<CompaniesPage />} />
                  <Route path='dashboard' element={<DashboardReportingPage />} />
                  <Route path='search' element={<GlobalSearchPage />} />
                  <Route path='financial-statements' element={<FinancialStatementsPage />} />
                  <Route path='process-hierarchy' element={<ProcessHierarchyPage />} />
                  <Route path='risk-assessment' element={<RiskAssessmentPage />} />
                  <Route path='risk-assessment/:id' element={<RiskAssessmentDetailPage />} />
                  <Route path='risk-assessment/:id/:tab' element={<RiskAssessmentDetailPage />} />
                  <Route path='risk-assessment-total' element={<RiskAssessmentTotalPage />} />
                  <Route path='risk-assessment-total/:tab' element={<RiskAssessmentTotalPage />} />
                  <Route path='control-activities' element={<ControlActivitiesPage />} />
                  <Route path='control-activities/manage-weights' element={<BulkWeightingManagementPage />} />
                  <Route path='control-activities/create' element={<ControlActivityItemPage />} />
                  <Route path='control-activities/:controlId' element={<ControlActivityItemPage />} />
                  <Route path='gaps' element={<GapsPage />} />
                  <Route path='gaps/create' element={<GapItemPage />} />
                  <Route path='gaps/:gapId' element={<GapItemPage />} />
                  <Route path='certification' element={<CertificationPage />} />
                  <Route path='coso-compliance' element={<COSOCompliancePage />} />
                  <Route path='audit-plan' element={<AuditPlanPage />} />
                  <Route path='user-management' element={<UserManagementPage />} />
                  <Route path='settings' element={<AccountSettingsPage />} />
                  <Route path='system-settings' element={<AssertionManagementPage />} />
                  <Route path='/' element={<Navigate to='/dashboard' />} />
                  <Route path='*' element={<NotFoundPage />} />
                </Routes>
              </DashboardLayout>
              )
            )) : (
              <Navigate to='/auth/signin' replace />
            )
          }
        />
      </Routes>
    </div>
  );
}

export default App; 